import { MealPlanType } from "libs/api/customer/src/lib/types/cats";

import {
  ALL_MEAL_PLANS,
  MEAL_PLAN_FREEZER,
  MEAL_PLAN_FREEZER_CODE,
  MEAL_PLAN_HALF,
  MEAL_PLAN_HALF_CODE,
  MEAL_PLAN_SAVER,
  MEAL_PLAN_SAVER_CODE,
  MEAL_PLAN_STANDARD_CODE,
} from "./mealPlanSuggestions";
import { CancellationReason } from "./cancellation-reason";

export const getCurrentMealPlan = (currentMealPlan: MealPlanType) => {
  // First check if it's in the ALL_MEAL_PLANS array
  const planFromArray = ALL_MEAL_PLANS.find((mealPlan) => mealPlan.value === currentMealPlan);
  if (planFromArray) {
    return planFromArray;
  }

  // If not found in ALL_MEAL_PLANS, check individual plan objects
  // This handles cases like MEAL_PLAN_FREEZER which is not in ALL_MEAL_PLANS
  if (currentMealPlan === MEAL_PLAN_FREEZER_CODE) {
    return MEAL_PLAN_FREEZER;
  }

  return undefined;
};

export function getMealPlanSuggestions({
  cancellationReason,
  currentMealPlanCode,
  selectedMealPlanCode,
}:{
  cancellationReason?: CancellationReason;
  currentMealPlanCode?: MealPlanType;
  selectedMealPlanCode?: MealPlanType;
}) {
  // if any of the required parameters are not provided, we fallback to suggesting all meal plans
  if (!currentMealPlanCode || !cancellationReason) {
    return ALL_MEAL_PLANS
      .map((mealPlan) => ({
        ...mealPlan,
        advanced_options: { ...mealPlan.advanced_options, current: mealPlan.value === currentMealPlanCode },
        selected: selectedMealPlanCode === mealPlan.value,
      }));
  }

  const currentMealPlan = getCurrentMealPlan(currentMealPlanCode);

  // if the current meal plan is not found, we fallback to suggesting all meal plans
  if (!currentMealPlan) {
    return ALL_MEAL_PLANS
      .map((mealPlan) => ({
        ...mealPlan,
        advanced_options: { ...mealPlan.advanced_options, current: mealPlan.value === currentMealPlanCode },
        selected: selectedMealPlanCode === mealPlan.value,
      }));
  }

  // If the user is cancelling because the meal plan is too expensive and they are not on the saver plan,
  // we suggest their current meal plan and the saver plan
  if (
    cancellationReason === CancellationReason.TOO_EXPENSIVE_OVER_MY_BUDGET &&
    currentMealPlanCode !== MEAL_PLAN_SAVER_CODE
  ) {
    return [
      {
        ...currentMealPlan,
        selected: selectedMealPlanCode === currentMealPlan.value,
        advanced_options: {
          ...currentMealPlan.advanced_options,
          current: currentMealPlan.value === currentMealPlanCode,
        },
      },
      {
        ...MEAL_PLAN_SAVER,
        selected: selectedMealPlanCode === MEAL_PLAN_SAVER.value,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive and they are on the saver plan,
  // we suggest the half meal plan and their current meal plan
  if (
    cancellationReason === CancellationReason.TOO_EXPENSIVE_OVER_MY_BUDGET &&
    currentMealPlanCode === MEAL_PLAN_SAVER_CODE
  ) {
    return [
      {
        ...currentMealPlan,
        selected: selectedMealPlanCode === currentMealPlan.value,
        advanced_options: {
          ...currentMealPlan.advanced_options,
          current: currentMealPlan.value === currentMealPlanCode,
        },
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlanCode === MEAL_PLAN_HALF.value,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive compared to other brands
  // and they are on the standard or freezer plan, we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReason.TOO_EXPENSIVE_OTHER_BRANDS_ARE_CHEAPER &&
      (currentMealPlanCode === MEAL_PLAN_STANDARD_CODE || currentMealPlanCode === MEAL_PLAN_FREEZER_CODE)
  ) {
    return [
      {
        ...currentMealPlan,
        selected: selectedMealPlanCode === currentMealPlan.value,
        advanced_options: {
          ...currentMealPlan.advanced_options,
          current: currentMealPlan.value === currentMealPlanCode,
        },
      },
      {
        ...MEAL_PLAN_SAVER,
        selected: selectedMealPlanCode === MEAL_PLAN_SAVER.value,
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlanCode === MEAL_PLAN_HALF.value,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive compared to other brands
  // and they are on any other meal plan, we suggest all meal plans
  if (cancellationReason === CancellationReason.TOO_EXPENSIVE_OTHER_BRANDS_ARE_CHEAPER) {
    return ALL_MEAL_PLANS.map((mealPlan) => ({
      ...mealPlan,
      selected: selectedMealPlanCode === mealPlan.value,
      advanced_options: {
        ...mealPlan.advanced_options,
        current: mealPlan.value === currentMealPlanCode,
      },
    }));
  }

  // If the user is cancelling because they think it's not worth the cost
  // and they are on the standard or freezer plan,
  // we suggest their current meal plan, the half meal plan, and the saver plan
  if (
    cancellationReason === CancellationReason.TOO_EXPENSIVE_NOT_WORTH_THE_COST &&
    (currentMealPlanCode === MEAL_PLAN_STANDARD_CODE || currentMealPlanCode === MEAL_PLAN_FREEZER_CODE)
  ) {
    return [
      {
        ...currentMealPlan,
        selected: selectedMealPlanCode === currentMealPlan.value,
        advanced_options: {
          ...currentMealPlan.advanced_options,
          current: currentMealPlan.value === currentMealPlanCode,
        },
      },
      {
        ...MEAL_PLAN_SAVER,
        selected: selectedMealPlanCode === MEAL_PLAN_SAVER.value,
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlanCode === MEAL_PLAN_HALF.value,
      },
    ];
  }

  // If the user is cancelling because they think it's not worth the cost
  // and they are on any other meal plan, we suggest all meal plans
  if (cancellationReason === CancellationReason.TOO_EXPENSIVE_NOT_WORTH_THE_COST) {
    return ALL_MEAL_PLANS.map((mealPlan) => ({
      ...mealPlan,
      selected: selectedMealPlanCode === mealPlan.value,
      advanced_options: {
        ...mealPlan.advanced_options,
        current: mealPlan.value === currentMealPlanCode,
      },
    }));
  }

  // If the user is cancelling because they have accumulated too much meal in their freezer
  // and they are on any plan except the freezer plan, we suggest their current meal plan and the freezer plan
  if (
    cancellationReason === CancellationReason.FREEZER_SPACE_ACCUMULATION &&
    currentMealPlanCode !== MEAL_PLAN_FREEZER_CODE
  ) {
    return [
      {
        ...currentMealPlan,
        selected: selectedMealPlanCode === currentMealPlan.value,
        advanced_options: {
          ...currentMealPlan.advanced_options,
          current: currentMealPlan.value === currentMealPlanCode,
        },
      },
      {
        ...MEAL_PLAN_FREEZER,
        selected: selectedMealPlanCode === MEAL_PLAN_FREEZER.value,
      },
    ];
  }

  // If the user is cancelling because they have accumulated too much meal in their freezer
  // and they are on the freezer plan, we suggest all meal plans except the freezer plan
  if (cancellationReason === CancellationReason.FREEZER_SPACE_ACCUMULATION) {
    return ALL_MEAL_PLANS
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlanCode === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlanCode,
        },
      }));
  }

  // if the user is cancelling because they don't have enough space in their freezer
  // and they are on any plan except the freezer plan, we suggest their current meal plan and the freezer plan
  if (
    cancellationReason === CancellationReason.FREEZER_SPACE_NOT_ENOUGH_SPACE &&
    currentMealPlanCode !== MEAL_PLAN_FREEZER_CODE
  ) {
    return [
      {
        ...currentMealPlan,
        selected: selectedMealPlanCode === currentMealPlan.value,
        advanced_options: {
          ...currentMealPlan.advanced_options,
          current: currentMealPlan.value === currentMealPlanCode,
        },
      },
      {
        ...MEAL_PLAN_FREEZER,
        selected: selectedMealPlanCode === MEAL_PLAN_FREEZER.value,
      },
    ];
  }

  // If the user is cancelling because they don't have enough space in their freezer
  // and they are on the freezer plan, we suggest all meal plans except the freezer plan
  if (cancellationReason === CancellationReason.FREEZER_SPACE_NOT_ENOUGH_SPACE) {
    return ALL_MEAL_PLANS
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlanCode === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlanCode,
        },
      }));
  }

  // If the user is cancelling because they think the subscription doesn't work for them and they need less food
  // and they are on any other meal plan except the half meal plan,
  // we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReason.SUBSCRIPTION_DOESNT_WORK_NEED_LESS_FOOD &&
    currentMealPlanCode !== MEAL_PLAN_HALF_CODE
  ) {
    return [
      {
        ...currentMealPlan,
        selected: selectedMealPlanCode === currentMealPlan.value,
        advanced_options: {
          ...currentMealPlan.advanced_options,
          current: currentMealPlan.value === currentMealPlanCode,
        },
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlanCode === MEAL_PLAN_HALF.value,
      },
    ];
  }

  // If the user is cancelling because they think the subscription doesn't work for them and they need less food
  // and they are on the half meal plan, we suggest all meal plans except the half meal plan
  if (cancellationReason === CancellationReason.SUBSCRIPTION_DOESNT_WORK_NEED_LESS_FOOD) {
    return ALL_MEAL_PLANS
      .filter((mealPlan) => mealPlan.value !== MEAL_PLAN_HALF_CODE)
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlanCode === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlanCode,
        },
      }));
  }

  // if the user is cancelling but they plan to to come back as
  // they are currently feeding their cat with other foods alongside Katkin
  // and they are on any plan except the half meal plan, we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReason.COMING_BACK_FEEDING_OTHER_FOODS_ALONGSIDE_KATKIN &&
    currentMealPlanCode !== MEAL_PLAN_HALF_CODE
  ) {
    return [
      {
        ...currentMealPlan,
        selected: selectedMealPlanCode === currentMealPlan.value,
        advanced_options: {
          ...currentMealPlan.advanced_options,
          current: currentMealPlan.value === currentMealPlanCode,
        },
      },
      {
        ...MEAL_PLAN_HALF,
        selected: selectedMealPlanCode === MEAL_PLAN_HALF.value,
      },
    ];
  }

  // if the user is cancelling but they plan to come back as
  // they are currently feeding their cat with other foods alongside Katkin
  // and they are on the half meal plan, we suggest all meal plans except the half meal plan
  if (cancellationReason === CancellationReason.COMING_BACK_FEEDING_OTHER_FOODS_ALONGSIDE_KATKIN) {
    return ALL_MEAL_PLANS
      .filter((mealPlan) => mealPlan.value !== MEAL_PLAN_HALF_CODE)
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlanCode === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlanCode,
        },
      }));
  }

  // if the user is cancelling for any other reason, we suggest all meal plans
  return ALL_MEAL_PLANS.map((mealPlan) => ({
    ...mealPlan,
    selected: selectedMealPlanCode === mealPlan.value,
    advanced_options: {
      ...mealPlan.advanced_options,
      current: mealPlan.value === currentMealPlanCode,
    },
  }));
}
